import React, { useEffect, useMemo, useState } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { PhoneNumberInput, PhoneNumberInputValues } from "artisn-ui-react";
import { TextInput } from "artisn-ui-react";
import { useCountries, useInvestForm } from "@anefi/contexts";
import { useFetchCantones } from "@anefi/services";
import { useFetchProvinces, useFetchUser } from "@anefi/services";

import Styles from "./StepFourForm.styles";
import { StepFourFormProps as Props } from "./StepFourForm.types";
import { StepFourFormValues } from "./StepFourForm.types";
import Checkbox from "components/global/Checkbox/Checkbox";
import useI18n from "hooks/useI18n";
import useAuth from "contexts/auth/auth.context.hooks";
import { convertDataToDataSelect } from "@anefi/utils";
import Select from "components/global/Select/Select";

const StepFourForm: React.FC<Props> = props => {
  const t = useI18n();
  const stepFourContext = useFormContext<StepFourFormValues>();
  const { register, formState, getValues } = stepFourContext;
  const { control, setValue } = stepFourContext;
  const { countryISOCodes, selectedCountry } = useCountries();
  const { isoCode: defaultCountryISOCode } = selectedCountry;
  const auth = useAuth();
  const { data: user } = useFetchUser(auth);
  const { additionalInfo } = user ?? {};
  const { investForm } = useInvestForm();
  const { homeAddressCountryCode } = investForm;
  const [additionalPhone, setAdditionalPhone] =
    useState<PhoneNumberInputValues>({
      countryCallingCode: "",
      countryCode: defaultCountryISOCode,
      international: "",
      internationalRaw: "",
      isPossible: true,
      isValid: true,
      national: getValues().additionalPhone ?? "",
      nationalRaw: ""
    });

  const watchProvince = useWatch({ control, name: "homeAddressProvinceCode" });
  const watchCanton = useWatch({ control, name: "homeAddressCantonCode" });

  const { data: provincesData } = useFetchProvinces(homeAddressCountryCode);
  const { data: cantonesData } = useFetchCantones(
    homeAddressCountryCode,
    watchProvince
  );

  const provincesForSelect = convertDataToDataSelect(
    provincesData,
    "code",
    "name"
  );
  const cantonesForSelect = convertDataToDataSelect(
    cantonesData,
    "code",
    "name"
  );

  const provinceName = useMemo(
    () =>
      provincesForSelect.find(item => item.key === watchProvince)?.value ?? "",
    [provincesForSelect, watchProvince]
  );
  const cantonName = useMemo(
    () => cantonesForSelect.find(item => item.key === watchCanton)?.value ?? "",
    [cantonesForSelect, watchCanton]
  );

  // TODO: Uncomment when province and other field are required
  // const { data: addressData } = useFetchAddressTypes();

  // TODO: Uncomment when province and other field are required
  // const dataForSelect = useMemo(
  //   () => convertDataToDataSelect(addressData, "code", "name"),
  //   [addressData]
  // );

  const errors = formState?.errors ?? {};
  const { homeAddressMainStreet, homeAddressProvinceCode } = errors;
  const { additionalPhone: additionalPhoneError } = errors;
  const { homePhoneType, homeAddressCantonCode } = errors;
  const disabledAddressFiscal = additionalInfo?.IshomeAddressFiscal === "S";
  const addressFiscalStyle = `fieldset ${
    disabledAddressFiscal ? "StepFourForm__checkbox-disabled" : ""
  }`;

  useEffect(() => {
    setValue("homeAddressProvinceName", provinceName);
  }, [provinceName, setValue]);

  useEffect(() => {
    setValue("homeAddressCantonName", cantonName);
  }, [cantonName, setValue]);

  return (
    <Styles className="StepFourForm">
      <p className="StepFourForm__title">Información del domicilio actual</p>
      <Controller
        name="homeAddressProvinceCode"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            className="field"
            selectData={provincesForSelect}
            label={t.stepFourForm.province}
            placeholder={t.stepFourForm.province}
            errorMessage={homeAddressProvinceCode?.message}
          />
        )}
      />
      <Controller
        name="homeAddressCantonCode"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            className="field"
            selectData={cantonesForSelect}
            label={t.stepFourForm.canton}
            placeholder={t.stepFourForm.canton}
            errorMessage={homeAddressCantonCode?.message}
          />
        )}
      />
      <TextInput
        {...register("homeAddressMainStreet")}
        className="field"
        label={t.commonFieldForm.address}
        placeholder={t.commonFieldForm.address}
        errorMessage={homeAddressMainStreet?.message}
      />
      {/* TODO: Uncomment when province and other field are required */}
      {/* <Select
        {...register("addressType")}
        className="StepFourForm__select"
        selectData={dataForSelect}
        label={t.commonFieldForm.addressType}
        placeholder={t.commonFieldForm.addressType}
        errorMessage={addressType?.message}
      /> */}
      <fieldset disabled={disabledAddressFiscal} className={addressFiscalStyle}>
        <Checkbox
          {...register("IshomeAddressFiscal")}
          className="field"
          label={t.stepFourForm.IshomeAddressFiscal}
          defaultChecked={disabledAddressFiscal}
        />
      </fieldset>
      <TextInput
        {...register("homePhoneType")}
        className="field"
        label={t.stepOneForm.phoneType}
        errorMessage={homePhoneType?.message}
        disabled
      />
      <Controller
        name="additionalPhone"
        control={control}
        render={({ field }) => (
          <PhoneNumberInput
            {...field}
            className="field"
            label="Teléfono"
            countriesCodes={countryISOCodes}
            onChange={e => {
              setAdditionalPhone(e);
              field.onChange(e.national);
            }}
            errorMessage={additionalPhoneError?.message}
            inputValue={additionalPhone.national}
            maxLength={11}
            placeholder="022520890"
          />
        )}
      />
    </Styles>
  );
};

StepFourForm.defaultProps = {};

export default StepFourForm;
